// @ts-check
// @ts-ignore

import { camelToPascal, snakeToCamel } from '@/common/utils/strings';
import { closeModal, getModal, openModal } from './action';

/**
 * @typedef {object} ModalSpecOptions
 * @property {number}  size
 * @property {boolean}  useOnAsPrefix
 * @property {boolean}  omitModalSuffixOnDispatch
 */

/**
 * @param {string} id
 * @param {string} [name] modal name in camelCase
 * @param {number | Partial<ModalSpecOptions>} [options]
 */
const parseModalSpec = (id, name, options) => {

  if (typeof options === 'number') {
    options = { size: options };
  }

  const {
    size = 600,
    useOnAsPrefix = false,
    omitModalSuffixOnDispatch = false,
  } = options || {};

  name = name?.trim();
  if (!name) {
    const idArray = id.split(/[/\\]/g).filter((x) => !!x);
    const lastElement = idArray[idArray.length - 1]?.trim() ?? '';
    name = snakeToCamel(lastElement.toLowerCase());
  }
  if (!name) {
    throw Error(
      `Please assign a valid modal name instead of ${JSON.stringify(name)}.`
    );
  }

  const needsStripModalSuffix = omitModalSuffixOnDispatch && name.toLowerCase().endsWith('modal');
  const nameForDispatch = camelToPascal(needsStripModalSuffix ? name.slice(0, -5) : name);
  const defaultSize = size > 0 ? size : 600;

  // Cache the action names to avoid recalculation
  const open = (useOnAsPrefix ? 'onOpen' : 'open') + nameForDispatch;
  const close = (useOnAsPrefix ? 'onClose' : 'close') + nameForDispatch;
  const metadata = { name, open, close };

  return { defaultSize, metadata, nameForDispatch };
};

/**
 * @template {any} T
 * @typedef {object} ModalState
 */

/**
 * @template {{}} T
 * @param {string} id
 * @param {string} [name] modal name in camelCase
 * @param {number | Partial<ModalSpecOptions>} [options]
 * @returns {[
 *  (state: any) => { [modal: string]: ModalState<T>; },
 *  (dispatch: (any: any) => any) => { [open: string]: (props: any, size?: number) => void; [close: string]: () => void; },
 *  (state: any) => T,
 *  { name: string; open: string; close: string; }
 * ]}
 */
export const useModal = (id, name, options) => {
  const { defaultSize, metadata } = parseModalSpec(id, name, options);
  const { name: parsedName, open, close } = metadata;

  /**
   * @param {any} state
   * @returns
   */
  const mapState = (state) => ({
    [parsedName]: getModal(state, id) || {},
  });

  /**
   * @param {(any) => any} dispatch
   * @returns
   */
  const mapDispatch = (dispatch) => ({
    [open](
      props,
      size = defaultSize
    ) {
      dispatch(openModal(id, props || {}, size));
    },

    [close](event) {
      if (event instanceof Event) {
        event.preventDefault();
      }
      dispatch(closeModal(id));
    },
  });

  /**
   * @param {any} state
   * @returns
   */
  const useProps = (state) => getModal(state, id).props ?? {};

  // @ts-ignore
  return [mapState, mapDispatch, useProps, metadata];
};

/**
 * @template {{}} T
 * @param  {...(string | [string, ...any] | ModalSpecOptions)} args
 * @returns {[
 *  (state: any) => { [modal: string]: ModalState<T>; },
 *   (dispatch: (any: any) => any) => { [open: string]: (props: any, size?: number) => void; [close: string]: () => void; },
 *   (state: any) => T,
 *  { name: string; open: string; close: string; }[]
 * ]}
 */
// @ts-ignore
export const useModals = (...args) => {
  // @ts-ignore
  // Check if the last argument is an object (default options)

  /** @type {ModalSpecOptions | null} */
  let defaultOptions = null;
  let modalArgs = args;

  if (args.length > 0) {
    const lastArg = args[args.length - 1];
    // If last arg is a plain object and not a string or array, treat it as default options
    if (lastArg && typeof lastArg === 'object' && !Array.isArray(lastArg) && typeof lastArg !== 'string') {
      // Check if it's not a modal config array by verifying first element is not a string
      if (!lastArg[0] || typeof lastArg[0] !== 'string') {
        defaultOptions = lastArg;
        modalArgs = args.slice(0, -1);
      }
    }
  }

  // Cache the modal results to avoid recalculation
  const res = modalArgs.map((arg) => {
    if (typeof arg === 'string') {
      return useModal(arg, undefined, defaultOptions ?? undefined);
    } else {
      const [id, name, options] = arg;
      const mergedOptions = defaultOptions
        ? typeof options === 'number'
          ? { ...defaultOptions, size: options }
          : { ...defaultOptions, ...options }
        : options;
      return useModal(id, name, mergedOptions);
    }
  });

  // Pre-compute the metadata to avoid repeated mapping
  const metadata = res.map(m => m[3]);

  // @ts-ignore  
  return [
    // Optimize state mapping by using a more efficient reduce
    (state) => {
      const result = {};
      for (const modal of res) {
        Object.assign(result, modal[0](state));
      }
      return result;
    },
    // Optimize dispatch mapping
    // @ts-ignore
    (dispatch) => {
      const result = {};
      for (const modal of res) {
        Object.assign(result, modal[1](dispatch));
      }
      return result;
    },
    // Optimize props mapping
    // @ts-ignore
    (state) => {
      const result = {};
      for (const modal of res) {
        Object.assign(result, modal[2](state));
      }
      return result;
    },
    metadata,
  ];
};

const initModalState = (size = 600) => ({
  show: false,
  size,
  props: {},
});

/**
 * @typedef {object} EmbeddedModalHost
 * @property {any} state
 * @property {() => {}} update
 */

/**
 * @template {{}} T
 * @param {string} [name] modal name in camelCase
 * @param {number | Partial<ModalSpecOptions>} [options]
 */
export const useEmbeddedModal = (name, options) => {
  const { defaultSize, metadata, nameForDispatch } = parseModalSpec('', name || 'modal', options);
  const { name: parsedName, open, close } = metadata;
  const init = 'init' + nameForDispatch;
  const bind = 'bind' + nameForDispatch;

  /** @this {EmbeddedModalHost} */
  function initFunc() {
    this.state[parsedName] ??= initModalState(defaultSize);
  }

  /** @this {EmbeddedModalHost} */
  function bindFunc() {
    return { modal: this.state[parsedName] };
  }

  /** @this {EmbeddedModalHost} */
  function openFunc(props = {}, size = defaultSize) {
    this.state[parsedName] = {
      ...(this.state[parsedName]),
      show: true,
      size,
      props,
    };
    this.update();
  }

  /** @this {EmbeddedModalHost} */
  function closeFunc() {
    this.state[parsedName] = {
      ...(this.state[parsedName]),
      show: false,
      props: {}
    };
    this.update();
  }

  return {
    [open]: openFunc,
    [close]: closeFunc,
    [init]: initFunc,
    [bind]: bindFunc,
  };

};